import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import api, { timeAgo } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { track } from "@/components/layernav/navConfig";
import { ProtocolRow } from "@/components/sector/ProtocolActivity";

export default function DeFiProtocolsPage() {
  const [cat, setCat] = useState<string>("All");
  const { data, isLoading } = useQuery({
    queryKey: ["defi-protocols"],
    queryFn: async () => (await api.get("/defi/protocols")).data,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    track("sector_page_view", { sector: "defi", target: "protocol_rankings" });
  }, []);

  const protocols: any[] = (data?.protocols || []).filter(
    (p: any) => cat === "All" || p.category === cat);
  const categories: string[] = ["All", ...(data?.categories || [])];

  return (
    <div data-testid="defi-protocols-page" className="pb-16">
      <Seo title="DeFi Protocol Rankings — Live TVL — NewsLayer"
        description="Live TVL rankings of eligible DeFi protocols with 24h and 7-day changes, chains and token movement. Deterministic NewsLayer eligibility, data from DeFiLlama and Kraken."
        path="/defi/protocols" />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-8">
        <Link to="/defi" data-testid="protocols-back-link"
          className="inline-flex items-center gap-1.5 font-mono text-[11px] font-bold text-[#34D399] hover:text-white transition-colors">
          <ArrowLeft className="h-3.5 w-3.5" /> DeFi Sector
        </Link>
        <h1 className="font-heading font-black text-white text-3xl sm:text-4xl tracking-tight mt-3">
          Protocol Rankings
        </h1>
        <p className="text-gray-400 text-[14px] mt-2 max-w-2xl leading-relaxed">
          Top DeFi protocols by live TVL. Deterministic NewsLayer eligibility — CEX, bridge, chain and
          meta-aggregate entries excluded; versioned deployments aggregated under their parent protocol.
        </p>

        <div className="mt-6 flex flex-wrap gap-2" data-testid="protocols-filters">
          {categories.map((c) => (
            <button key={c} data-testid={`protocols-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => setCat(c)}
              className={`rounded-full border px-3.5 py-1.5 font-mono text-[11px] font-bold transition-colors ${
                cat === c
                  ? "border-[#34D399] bg-[#34D399]/15 text-[#34D399]"
                  : "border-white/[0.14] bg-white/[0.04] text-gray-300 hover:border-[#34D399]/50 hover:text-white"}`}>
              {c}
            </button>
          ))}
        </div>

        <div className="mt-5 rounded-xl border border-white/[0.08] bg-[#0b0c10] p-4 md:p-5">
          {isLoading ? (
            <div className="h-[420px] rounded-lg bg-white/[0.02] animate-pulse" aria-hidden />
          ) : protocols.length ? (
            <div className="divide-y divide-white/[0.04]" data-testid="protocols-table">
              {protocols.map((p) => <ProtocolRow key={p.slug} p={p} testPrefix="rankings" nav />)}
            </div>
          ) : (
            <p data-testid="protocols-empty" className="font-mono text-[12px] text-gray-500 py-8 text-center">
              No protocols in this category right now.
            </p>
          )}
          <p className="mt-3 font-mono text-[8.5px] uppercase tracking-wider text-gray-500"
            title={data?.eligibility?.rule}>
            Live TVL rankings · DeFiLlama{data?.updated_at ? ` · Updated ${timeAgo(data.updated_at)}` : ""} · 7D trend from NewsLayer-owned series · Tokens via Kraken
          </p>
        </div>
      </div>
    </div>
  );
}
